import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  InputPropsStrict,
  TablePageChangeEvent,
  TableProps,
} from '@servicetitan/design-system';
import {
  getUsersForOutboundModalEmployeeTable,
  UserQueryItem,
} from '@api/queries/getUsersForOutboundModalEmployeeTable';
import useService from '@modules/ServiceModule/useService';
import { ServiceUserUtils } from '@modules/ServiceModule/workspace/lib';

export const useEmployeeTableState = () => {
  const serviceState = useService();
  const [users, setUsers] = useState<UserQueryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState({ skip: 0, take: 4 });
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const searchString = useMemo(() => search.trim().toLowerCase(), [search]);

  // fetch users from api
  const fetchUsers = async () => {
    try {
      const users = await getUsersForOutboundModalEmployeeTable();
      setUsers(users);
    } catch (_e) {
      setUsers([]); // todo: talk to the product team about what to do in case of an error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    setPage((prevPage) => ({ skip: 0, take: prevPage.take }));
  }, [searchString]);

  const dataFiltered = useMemo(() => {
    return !searchString
      ? [...users]
      : users.filter((user) =>
          [user.name, user.email, user.personalLine?.phoneNumber].some(
            (value) => value?.toLowerCase().includes(searchString),
          ),
        );
  }, [searchString, users]);

  const dataPaginated = useMemo(
    () => dataFiltered.slice(page.skip, page.skip + page.take),
    [dataFiltered, page],
  );

  const data = useMemo(
    () =>
      dataPaginated.map((user) => ({
        id: user.id,
        name: user.name,
        phone: user.personalLine?.phoneNumber ?? '',
        status: serviceState.userById[user.id]
          ? ServiceUserUtils.getAvailabilityStatus(
              serviceState.userById[user.id],
            )
          : undefined, // append status from the ServiceModule when available
      })),
    [dataPaginated, serviceState.userById],
  );

  const onSearchChange: InputPropsStrict['onChange'] = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [],
  );

  const onPageChange: TableProps['onPageChange'] = useCallback(
    (event: TablePageChangeEvent) => {
      setPage(event.page);
    },
    [],
  );

  const onSelectionChange = (id: string) => {
    setSelectedId(id);
  };

  return {
    data,
    onPageChange,
    onSearchChange,
    onSelectionChange,
    search,
    loading,
    page,
    selectedId,
    total: dataFiltered.length,
  };
};
